import { Modal } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import Styles from "./ActionModal.module.css";
import { Close } from "@mui/icons-material";
import { ReactComponent as Permissions } from "../../../../assets/images/permissions.svg";
import { ReactComponent as EditIcon } from "../../../../assets/images/editsolid.svg";
import PermissionModal from "../PermissionModal/PermissionModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConsumerUsers,
  fetchGeneratorUsers,
} from "../../../../Redux/Slices/companyAdminSlice";
import { changeUserActiveStatus } from "../../../../Services/CompanyAdmin/AllApiAdmin";
import { changeUserActiveStatusSuperAdmin } from "../../../../Services/GNA/AlterApi";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import AddUserModal from "../AddUserModal/AddUserModal";
import { toast } from "react-toastify";

const UserActions = ({
  user,
  isActive,
  onEdit,
  onSetPermission,
  onToggle,
  disabledPlaceholder = false,
}) => {
  const btnStyle = {
    width: "15%",
    textAlign: "center",
    cursor: isActive ? "pointer" : "not-allowed",
    opacity: isActive ? 1 : 0.6,
  };

  if (disabledPlaceholder) {
    return (
      <div style={btnStyle} className={Styles.userBtns}>
        <span></span>
        <span></span>
        <div style={{ textAlign: "center" }}>-</div>
      </div>
    );
  }

  return (
    <div style={btnStyle} className={Styles.userBtns}>
      <span
        onClick={isActive ? onEdit : undefined}
        className={!isActive ? Styles.disabledIcon : ""}
        title={isActive ? "Edit User" : "Edit disabled"}
        aria-disabled={!isActive}
      >
        <EditIcon style={{ width: "21px", height: "16px" }} />
      </span>
      <span
        onClick={isActive ? onSetPermission : undefined}
        className={!isActive ? Styles.disabledIcon : ""}
        title={isActive ? "Set Permissions" : "Permissions disabled"}
        aria-disabled={!isActive}
      >
        <Permissions style={{ width: "24px", height: "16px" }} />
      </span>
      <div style={{ textAlign: "center" }}>
        <label className={Styles.switch}>
          <input type="checkbox" checked={isActive} onChange={onToggle} />
          <span className={Styles.slider}></span>
        </label>
      </div>
    </div>
  );
};

const ActionModal = ({
  open,
  handleClose,
  selectedEntity,
  isSuperAdmin = false,
}) => {
  const [openPermission, setPermission] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [selectedUserStatus, setSelectedUserStatus] = useState(true);

  const entityId =
    selectedEntity?.entityType === "CONSUMER"
      ? selectedEntity?.consumerId
      : selectedEntity?.entityType === "GENERATOR"
      ? selectedEntity?.generatorId
      : selectedEntity?.discomId;

  const entityType =
    selectedEntity?.accountType || selectedEntity?.entityType || "Unknown Type";

  const dispatch = useDispatch();

  const { consumerUsers, generatorUsers } = useSelector(
    (state) => state.companyAdminSlice
  );

  const currentUsers = useMemo(() => {
    if (selectedEntity?.entityType === "CONSUMER") {
      return consumerUsers || [];
    } else if (selectedEntity?.entityType === "GENERATOR") {
      return generatorUsers || [];
    }
    return [];
  }, [selectedEntity?.entityType, consumerUsers, generatorUsers]);

  useEffect(() => {
    if (entityId && selectedEntity?.entityType) {
      if (selectedEntity.entityType === "CONSUMER") {
        dispatch(fetchConsumerUsers(entityId));
      } else if (selectedEntity.entityType === "GENERATOR") {
        dispatch(fetchGeneratorUsers(entityId));
      }
    }
  }, [dispatch, entityId, selectedEntity?.entityType]);

  useEffect(() => {
    const initialStatus = {};
    if (currentUsers && Array.isArray(currentUsers)) {
      currentUsers.forEach((user) => {
        const isActive = user.isActive ?? user.status ?? true;
        initialStatus[user.id || user.userId] = isActive;
      });
    }
    setUserStatus(initialStatus);
  }, [currentUsers]);

  const handleToggle = (userId) => {
    const user = currentUsers.find((u) => (u.id || u.userId) === userId);
    const currentStatus = userStatus[userId];

    if (!user) {
      toast.error("User not found.");
      return;
    }

    setSelectedUser(user);
    setSelectedUserStatus(currentStatus);
    setOpenDelete(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      const newStatus = !selectedUserStatus;
      const userId = selectedUser.id || selectedUser.userId;

      let response;
      if (isSuperAdmin) {
        // Use super admin API - requires requesterUserId parameter
        const requesterUserId = "superadmin"; // You might want to get this from user context
        response = await changeUserActiveStatusSuperAdmin(
          userId,
          newStatus,
          requesterUserId
        );
      } else {
        // Use regular company admin API
        response = await changeUserActiveStatus(userId, newStatus);
      }

      if (response?.data?.error || response?.data?.code === 400) {
        const errorMessage =
          response.data.error || response.data.message || "Operation failed";
        toast.error(errorMessage);
        setOpenDelete(false);
        setSelectedUser(null);
        return;
      }

      setUserStatus((prev) => ({
        ...prev,
        [userId]: newStatus,
      }));

      if (selectedEntity?.entityType === "CONSUMER") {
        dispatch(fetchConsumerUsers(entityId));
      } else if (selectedEntity?.entityType === "GENERATOR") {
        dispatch(fetchGeneratorUsers(entityId));
      }

      const statusText = newStatus ? "activated" : "deactivated";
      toast.success(`User ${statusText} successfully!`);

      setOpenDelete(false);
      setSelectedUser(null);
    } catch (error) {

      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update user status. Please try again.";

      toast.error(errorMessage);
    }
  };

  return (
    <>
      {openPermission && (
        <PermissionModal
          open={openPermission}
          handleClose={() => {
            setPermission(false);
            setSelectedUser(null);
          }}
          entityId={entityId}
          entityType={selectedEntity?.entityType}
          selectedEntity={selectedEntity}
          selectedUser={selectedUser}
          userId={selectedUser?.id || selectedUser?.userId}
        />
      )}
      {openDelete && (
        <ConfirmationModal
          open={openDelete}
          handleClose={() => {
            setOpenDelete(false);
            setSelectedUser(null);
          }}
          header={selectedUserStatus ? "Deactivate User" : "Activate User"}
          data={
            selectedUserStatus
              ? `Are you sure you want to deactivate user "${
                  selectedUser?.userName || selectedUser?.email || "this user"
                }"?`
              : `Are you sure you want to activate user "${
                  selectedUser?.userName || selectedUser?.email || "this user"
                }"?`
          }
          handleSubmit={handleConfirmStatusChange}
        />
      )}
      {openAddUser && (
        <AddUserModal
          open={openAddUser}
          handleClose={() => {
            setOpenAddUser(false);
            setSelectedUser(null);
          }}
          isInAction={true}
          header={"Edit User"}
          userToEdit={selectedUser}
          entityId={entityId}
          entityType={selectedEntity?.entityType}
          isSuperAdmin={isSuperAdmin}
          onUserUpdated={() => {
            if (selectedEntity?.entityType === "CONSUMER") {
              dispatch(fetchConsumerUsers(entityId));
            } else if (selectedEntity?.entityType === "GENERATOR") {
              dispatch(fetchGeneratorUsers(entityId));
            }
          }}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className={`headingText ${Styles.headingSec}`}>
              List of Users <span className={Styles.secHead}>{entityType}</span>
            </div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
          <div
            className={`display_flex_row_center ${Styles.companyAdminMainContainer}`}
          >
            <div className={Styles.tableCompanyData}>
              <div
                className={`display_flex_row tableHeading ${Styles.tableCompanyHeader}`}
              >
                <div>Username</div>
                <div>Email Id</div>
                <div>Actions</div>
              </div>
            </div>
            <div className={Styles.tableMainContainer}>
              {currentUsers && currentUsers.length > 0 ? (
                currentUsers.map((user, index) => {
                  const userId = user.id || user.userId;
                  const isActive = userStatus[userId] === true;

                  const onEdit = () => {
                    setSelectedUser(user);
                    setOpenAddUser(true);
                  };

                  const onSetPermission = () => {
                    setSelectedUser(user);
                    setPermission(true);
                  };

                  const onToggle = () => handleToggle(userId);

                  return (
                    <div
                      key={userId || index}
                      className={`display_flex_row ${Styles.tableDataColumn}`}
                    >
                      <div
                        className={`display_flex_row ${Styles.tableRowData}`}
                      >
                        <div>{user.userName || "N/A"}</div>
                        <div>{user.email || "N/A"}</div>
                        {index !== 0 ? (
                          <UserActions
                            user={user}
                            isActive={isActive}
                            onEdit={onEdit}
                            onSetPermission={onSetPermission}
                            onToggle={onToggle}
                          />
                        ) : (
                          <UserActions
                            isActive={isActive}
                            disabledPlaceholder
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={`display_flex_row ${Styles.tableDataColumn}`}>
                  <div
                    className={`display_flex_row tableDetails ${Styles.tableRowData}`}
                    style={{ justifyContent: "center", padding: "20px" }}
                  >
                    <div>No users found for this {entityType}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ActionModal;
